<script setup lang="ts">
import {computed} from "vue";
import LegacyAccess from "@/core/interfaces/Access";
import {TeamAbsence} from "@/core/interfaces/TeamPlanning";
import {memberColor, memberInitials, memberName} from "@/team/planning";
import {ABSENCE_STATUS_BADGES, formatAbsenceDuration, formatAbsenceRange} from "@/planning/lib/absences";
import {Pencil, Trash2} from "@lucide/vue";

const {absence, member, editable = false, deletable = false} = defineProps<{
    absence: TeamAbsence;
    /** Absent when the member left the pharmacy since the absence was saved. */
    member?: LegacyAccess;
    editable?: boolean;
    deletable?: boolean;
}>();

defineEmits<{
    (event: 'edit'): void;
    (event: 'delete'): void;
}>();

const name = computed<string>(() => memberName(member));

const initials = computed<string>(() => memberInitials(member));

const color = computed<string>(() => memberColor(member));

const badge = computed(() => ABSENCE_STATUS_BADGES[absence.status] ?? null);
</script>

<template>
  <div class="flex items-center gap-3 rounded-lg border border-gray-100 px-3 py-2.5">
    <div
      class="flex size-9 flex-none items-center justify-center rounded-full text-[11px] font-semibold text-white"
      :style="{backgroundColor: color}"
    >
      {{ initials }}
    </div>

    <div class="min-w-0 flex-1">
      <div class="truncate text-xs font-semibold text-gray-800">
        {{ name }}
      </div>
      <!-- The stored reason is the motive's own label, so it reads right
           whether it came from the catalog or was typed in free text. -->
      <div class="truncate text-[10.5px] text-gray-400">
        {{ absence.reason }} · {{ formatAbsenceRange(absence) }}
      </div>
    </div>

    <div class="flex-none text-right">
      <div class="text-[11px] font-semibold text-gray-700">
        {{ formatAbsenceDuration(absence) }}
      </div>
      <span
        v-if="badge"
        class="mt-0.5 inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-bold"
        :class="badge.classes"
      >
        {{ badge.label }}
      </span>
    </div>

    <button
      v-if="editable"
      type="button"
      title="Modifier cette absence"
      class="flex size-7 flex-none items-center justify-center rounded-lg border border-solid border-gray-200 bg-white text-primary hover:border-primary"
      @click="$emit('edit')"
    >
      <Pencil class="size-3.5" />
    </button>

    <button
      v-if="deletable"
      type="button"
      title="Supprimer cette absence"
      class="flex size-7 flex-none items-center justify-center rounded-lg border border-solid border-gray-200 bg-white text-red-light hover:border-red-light"
      @click="$emit('delete')"
    >
      <Trash2 class="size-3.5" />
    </button>
  </div>
</template>
